import { useState, useEffect, useMemo } from 'react';
import usePatientsForDoctor from '../../../hooks/usePatientsForDoctor';
import useStudentsForOneTeacher from '../../../hooks/useStudentsForOneTeacher';
import useTeachersForOneStudent from '../../../hooks/useTeachersForOneStudent';
import useAllStudentBehaviorData from '../../../hooks/useAllStudentsBehaviorData';
import { usePreRegisteredUsers, useFullStudentGoals } from '../../../contexts/userDataContext';
import { fetchingTeachersForStudents } from '../../../api/teachers';
import { ACM } from '../../../constants/UserRoles';

const useUploadDataState = (user, typeOfUser, selectedStudent, selectedPatient) => {
  // Data state
  const [patientData, setPatientData] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [goalsDropDown, setGoalsDropDown] = useState([]);

  // Hooks - Using optimized data fetching
  const { data: patients = [] } = usePatientsForDoctor(user?.uid);
  const { data: students = [] } = useStudentsForOneTeacher(
    user?.uid,
    typeOfUser
  );
  const { data: teacherData = [] } = useTeachersForOneStudent(
    user?.uid,
    typeOfUser
  );
  const { data: preRegisteredUsers = [] } = usePreRegisteredUsers();
  const { data: studentGoalData = [] } = useFullStudentGoals();
  const { data: studentBehaviorDataRaw = [], isLoading: isLoadingBehaviorData, error: behaviorDataError } = useAllStudentBehaviorData(
    user?.uid,
    typeOfUser
  );
  
  // Normalize studentBehaviorData - handle different API response formats
  const studentBehaviorData = useMemo(() => {
    
    let dataArray = [];
    const isStudentOrPatient = ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient;
    
    // Handle case where API returns {status, message, studentData}
    if (
      studentBehaviorDataRaw.studentData &&
      Array.isArray(studentBehaviorDataRaw.studentData)
    ) {
      dataArray = studentBehaviorDataRaw.studentData;
      
    } else if (Array.isArray(studentBehaviorDataRaw)) {
      // If it's already an array, use it directly
      dataArray = studentBehaviorDataRaw;
      
    } else {
     
      return [];
    }
   
    
    // For students/patients, check if dataArray contains behavior documents (has docId) that need wrapping
    if (isStudentOrPatient && dataArray.length > 0) {
      const firstItem = dataArray[0];
     
      
      // If first item has docId and info, it's a behavior document - needs wrapping in student object
      // This happens when API returns raw behavior documents instead of student objects with behaviors array
      if (firstItem?.docId && firstItem?.info && !Array.isArray(firstItem?.behaviors)) {
       
        // Filter behaviors that belong to the student
        const validBehaviors = dataArray.filter((behavior) => {
          const hasValidStructure = behavior?.docId && behavior?.info && behavior?.info?.BehaviorDetails;
          const behaviorStudentID = behavior?.info?.StudentID;
          const belongsToStudent = behaviorStudentID === user?.uid;
          
          if (!hasValidStructure) {
            console.warn('🔍 studentBehaviorData: Skipping invalid behavior document', {
              hasDocId: !!behavior?.docId,
              hasInfo: !!behavior?.info,
              hasBehaviorDetails: !!behavior?.info?.BehaviorDetails,
            });
            return false;
          }
          
          if (!belongsToStudent) {
           
            return false;
          }
          
          return true;
        });
        
        if (validBehaviors.length === 0) {
          console.warn('🔍 studentBehaviorData: No valid behaviors found after filtering', {
            totalBehaviors: dataArray.length,
            userUid: user?.uid,
            firstBehaviorStudentID: dataArray[0]?.info?.StudentID,
          });
          return [];
        }
        
        const normalized = [
          {
            id: user?.uid,
            uid: user?.uid,
            studentId: user?.uid,
            behaviors: validBehaviors.map((behavior) => ({
              id: behavior.docId || behavior.id,
              docId: behavior.docId,
              ...behavior,
            })),
          },
        ];
       
        return normalized;
      }
      
      // If dataArray already has behaviors array but student ID doesn't match, fix it
      if (Array.isArray(firstItem?.behaviors)) {
        const studentId = firstItem.id || firstItem.uid || firstItem.studentId;
        if (studentId !== user?.uid) {
         
          return dataArray.map((item) => ({
            ...item,
            id: user?.uid,
            uid: user?.uid,
            studentId: user?.uid,
          }));
        }
      }
    }
  
    return dataArray;
  }, [studentBehaviorDataRaw, typeOfUser, user?.uid]);
  
 
  const studentsGoalData = useMemo(() => {
   
  
    
    return studentGoalData.map((item, index) => {
      const newItem = { ...item };
      
      // Handle student login structure (docId + studentData)
      if (item.docId && item.studentData && !Array.isArray(item.studentData)) {
       
        const goalInfo = item.studentData;
        const goalStudentId = goalInfo?.GoalDetails?.StudentID || goalInfo?.StudentID;
        const goalId = item.docId;
        
        // For students/patients: if this is their own data, ALWAYS normalize and include
        // Don't check ShareWithTeam - students should see all their own goals for video upload
        if (ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) {
          // Check if goal belongs to this student
          if (goalStudentId === user?.uid) {
          
            return {
              ...newItem,
              id: goalStudentId, // Use student's uid for filtering
              docId: goalId, // Preserve docId (goal ID)
              dataCollection: [
                {
                  id: goalId, // Goal ID
                  info: goalInfo,
                  goalData: goalInfo.goalData || null,
                  activityLog: goalInfo.activityLog || [],
                  isArchived: goalInfo.isArchived || false,
                },
              ],
            };
          } else {
            
            return null;
          }
        }
        
        // For teachers/doctors, check permissions but be lenient
        const { ShareWithTeam } = goalInfo;
        if (!ShareWithTeam) {
          // No sharing restrictions - normalize and include
          return {
            ...newItem,
            id: goalStudentId,
            docId: goalId,
            dataCollection: [
              {
                id: goalId,
                info: goalInfo,
                goalData: goalInfo.goalData || null,
                activityLog: goalInfo.activityLog || [],
                isArchived: goalInfo.isArchived || false,
              },
            ],
          };
        }
        
        const { EditAccess = [], ViewOnlyAccess = [] } = ShareWithTeam;
        const isOwner = goalInfo?.GoalDetails?.GoalAuthor === user.uid;
        const hasEditAccess = EditAccess.some((access) => access.uid === user.uid);
        const hasViewAccess = ViewOnlyAccess.some((access) => access.uid === user.uid);
        
        // Include if owner or has any access (for video upload context)
        if (isOwner || hasEditAccess || hasViewAccess || goalStudentId) {
        
          return {
            ...newItem,
            id: goalStudentId,
            docId: goalId,
            dataCollection: [
              {
                id: goalId,
                info: goalInfo,
                goalData: goalInfo.goalData || null,
                activityLog: goalInfo.activityLog || [],
                isArchived: goalInfo.isArchived || false,
              },
            ],
          };
        }
        
        return null;
      }
      
      // Handle teacher login structure (dataCollection array)
      if (Array.isArray(newItem.dataCollection)) {

        newItem.dataCollection = newItem.dataCollection.filter((collection) => {
          // Skip archived goals
          if (collection.isArchived === true) return false;
          
          // For students/patients viewing their own goals, show all
          if (ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) {
            return true;
          }
          
          const studentId = collection.info?.GoalDetails?.StudentID || collection.info?.StudentID;
          const isOwner = collection.info?.GoalDetails?.GoalAuthor === user.uid;
          
          // If goal has StudentID, include it (we'll verify student match later)
          if (studentId) {
            return true;
          }
          
          // If no ShareWithTeam, allow if teacher is owner
          if (!collection.info || !collection.info.ShareWithTeam) {
            return isOwner;
          }
          
          // Check sharing permissions
          const { EditAccess = [], ViewOnlyAccess = [] } = collection.info.ShareWithTeam;
          const hasEditAccess = EditAccess.some((access) => access.uid === user.uid);
          const hasViewAccess = ViewOnlyAccess.some((access) => access.uid === user.uid);
          
          return isOwner || hasEditAccess || hasViewAccess;
        });
        
       
      }
      
      return newItem;
    }).filter(Boolean); // Remove null items
  }, [studentGoalData, user?.uid, typeOfUser]);

  const behaviorData = useMemo(() => {
  
    
    if (!user?.uid) {
      return [];
    }
    
    // Handle empty or null studentBehaviorData
    if (!studentBehaviorData || !Array.isArray(studentBehaviorData) || studentBehaviorData.length === 0) {
      
      return [];
    }
    
    // For students/patients, show all behaviors that belong to them
    // For teachers/doctors, apply permission filtering
    const isStudentOrPatient = ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient;
    
    
    
    const processed = studentBehaviorData.map((item, index) => {
      const newItem = { ...item };
     
     
      if (isStudentOrPatient && newItem.docId && newItem.info && !Array.isArray(newItem.behaviors)) {
        const behaviorStudentID = newItem.info?.StudentID;
        const belongsToStudent = behaviorStudentID === user?.uid;
        
        if (!belongsToStudent) {
         
          return null; // Return null to filter out later
        }
        
        return {
          id: user?.uid,
          uid: user?.uid,
          behaviors: [{
            id: newItem.docId || newItem.id,
            docId: newItem.docId,
            ...newItem,
          }],
        };
      }
      
      if (Array.isArray(newItem.behaviors)) {
        // Get student ID from the parent structure - check multiple possible fields
        const studentId = newItem.id || newItem.uid || newItem.studentId || newItem.docId;
        
        // For students: also check if any behavior's StudentID matches user's uid
        // This handles cases where the student object ID might not match but behaviors belong to the student
        let behaviorBelongsToStudent = false;
        if (isStudentOrPatient && newItem.behaviors.length > 0) {
          behaviorBelongsToStudent = newItem.behaviors.some(behavior => {
            const behaviorStudentID = behavior?.info?.StudentID;
            return behaviorStudentID === user?.uid || behaviorStudentID === studentId;
          });
        }
        
        const studentMatches = isStudentOrPatient && (
          studentId === user?.uid || 
          behaviorBelongsToStudent ||
          newItem.behaviors.some(b => b?.info?.StudentID === user?.uid)
        );
        
        
        // For students/patients: if this student object belongs to the user, show ALL behaviors
        // Don't apply any permission filtering - students see all their own behaviors
        if (isStudentOrPatient && studentMatches) {
          // Return all behaviors without filtering - students should see ALL their own behaviors for video upload
          // This includes behaviors even if ShareWithTeam restrictions exist
          
          return newItem;
        }

        
        // For video upload: if teacher can access this student, show ALL behaviors
        // Check if this student is in the teacher's student list or matches selectedStudent
        const isAccessibleStudent = selectedStudent === studentId || 
                                    selectedPatient === studentId ||
                                    students.some(s => s.id === studentId) ||
                                    preRegisteredUsers.some(s => s.id === studentId);
        
        newItem.behaviors = newItem.behaviors.filter((behavior) => {
          if (!behavior.info) {
            return false;
          }
          
          // For video upload context: be lenient with permissions
          // If behavior belongs to a student the teacher has access to, show it
          const behaviorStudentID = behavior.info?.StudentID;
          
          // If no ShareWithTeam, check if teacher is owner OR if behavior belongs to accessible student
          if (!behavior.info.ShareWithTeam) {
            const isOwner = behavior.info?.BehaviorDetails?.BehaviorAuthor === user.uid;
            // For video upload: if teacher has access to the student, show all behaviors
            const belongsToAccessibleStudent = isAccessibleStudent || 
              (behaviorStudentID && (
                behaviorStudentID === selectedStudent || 
                behaviorStudentID === selectedPatient ||
                students.some(s => s.id === behaviorStudentID) ||
                preRegisteredUsers.some(s => s.id === behaviorStudentID)
              ));
            return isOwner || belongsToAccessibleStudent;
          }
          
          const { EditAccess = [], ViewOnlyAccess = [] } =
            behavior.info.ShareWithTeam;
          const isOwner = behavior.info?.BehaviorDetails?.BehaviorAuthor === user.uid;
          
          // Check approved access
          const hasEditAccess = EditAccess.some(
            (access) => access.uid === user.uid && access.approved === true
          );
          const hasViewAccess = ViewOnlyAccess.some(
            (access) => access.uid === user.uid && access.approved === true
          );
          
          // Also include pending access (approved: false) for video upload context
          const hasPendingEditAccess = EditAccess.some(
            (access) => access.uid === user.uid && access.approved === false
          );
          const hasPendingViewAccess = ViewOnlyAccess.some(
            (access) => access.uid === user.uid && access.approved === false
          );
          
          // For video upload: if behavior belongs to accessible student, also show it
          // This ensures teachers can see behaviors for students they have access to
          const belongsToAccessibleStudent = isAccessibleStudent || 
            (behaviorStudentID && (
              behaviorStudentID === selectedStudent || 
              behaviorStudentID === selectedPatient ||
              students.some(s => s.id === behaviorStudentID) ||
              preRegisteredUsers.some(s => s.id === behaviorStudentID)
            ));
          
          return isOwner || hasEditAccess || hasViewAccess || hasPendingEditAccess || hasPendingViewAccess || belongsToAccessibleStudent;
        });
        
        
      } else {
        
      }
      return newItem;
    }).filter(Boolean); // Remove null items (filtered out behaviors)
    
   
    
    
    
    return processed;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentBehaviorData, user?.uid, typeOfUser, selectedStudent, selectedPatient, students, preRegisteredUsers, studentBehaviorDataRaw, isLoadingBehaviorData, behaviorDataError]);


  const filteredBehaviors = useMemo(() => {
    let allBehaviors = [];
    const isStudentOrPatient = ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient;
    
    // Determine the target ID to match against
    const targetId = isStudentOrPatient
      ? user?.uid
      : ACM[typeOfUser]?.isTeacher
      ? selectedStudent
      : ACM[typeOfUser]?.isDoctor
      ? selectedPatient
      : null;

    behaviorData.forEach((student, studentIndex) => {
  
      if (!Array.isArray(student.behaviors)) {
      
        return;
      }
      
      // Get student ID from multiple possible fields
      const studentId = student.id || student.uid || student.studentId || student.docId;
      
      // For students/patients: if behaviorData already filtered to show only their behaviors,
      // include ALL behaviors from this student object - NEVER filter based on ShareWithTeam for students
      // Also check if any behavior's StudentID matches user's uid (more lenient matching)
      let shouldIncludeAll = isStudentOrPatient && (studentId === user?.uid);
      
      // Additional check: if student object ID doesn't match but behaviors belong to the student
      if (isStudentOrPatient && !shouldIncludeAll && Array.isArray(student.behaviors)) {
        shouldIncludeAll = student.behaviors.some(behavior => {
          const behaviorStudentID = behavior?.info?.StudentID;
          return behaviorStudentID === user?.uid;
        });
      }
      
      // For teachers/doctors: only process if this student matches the selected student
      if (!isStudentOrPatient && targetId && studentId !== targetId) {
       
        return; // Skip this student if it doesn't match
      }

      
      student.behaviors.forEach((behavior, behaviorIndex) => {
        const info = behavior?.info;
        const behaviorStudentID = info?.StudentID;
        
        // For students: ALWAYS include their own behaviors, regardless of ShareWithTeam
        // If the student object belongs to the user, include ALL behaviors in that object
        if (isStudentOrPatient && shouldIncludeAll) {
          // Since we already verified that studentId === user?.uid, 
          // ALL behaviors in this student object belong to the student
          // Include them regardless of individual behavior StudentID or ShareWithTeam
          if (info && info.BehaviorDetails && info.BehaviorDetails.Type) {
            const typeObj = info.BehaviorDetails.Type;
            const behaviorType = typeObj?.Type;
            
            if (behaviorType === 'Behavior' || behaviorType === 'Skill Acquisition') {
              // Ensure behavior has an id field - use docId if id doesn't exist
              const behaviorId = behavior.id || behavior.docId;
              if (!behaviorId) {
                console.warn(`🔍 filteredBehaviors: Behavior[${behaviorIndex}] has no id/docId - skipping`, {
                  behaviorKeys: Object.keys(behavior),
                });
                return;
              }
              
              allBehaviors.push({
                ...behavior,
                id: behaviorId, // Ensure id field exists
                studentName: info?.StudentName || '',
                behaviorStudentId: studentId,
                behaviorInfoStudentID: behaviorStudentID,
                fromStudentOwnData: true,
              });

            } else {
             
            }
          } else {
            
          }
          return; // Skip ShareWithTeam filtering for students - they see ALL their behaviors
        }
        
        // For teachers/doctors: apply ShareWithTeam filtering (already lenient)
        if (!info || !info.BehaviorDetails || !info.BehaviorDetails.Type) {
         
          return;
        }
        
        const typeObj = info.BehaviorDetails.Type;
        const behaviorType = typeObj?.Type;
        
        
        
        if (
          behaviorType === 'Behavior' ||
          behaviorType === 'Skill Acquisition'
        ) {
          // Ensure behavior has an id field - use docId if id doesn't exist
          const behaviorId = behavior.id || behavior.docId;
          if (!behaviorId) {
            console.warn(`🔍 filteredBehaviors: Behavior[${behaviorIndex}] has no id/docId - skipping`, {
              behaviorKeys: Object.keys(behavior),
            });
            return;
          }
          
          allBehaviors.push({
            ...behavior,
            id: behaviorId, // Ensure id field exists
            studentName: info?.StudentName || '',
            // Store the student's ID from the parent structure for matching
            behaviorStudentId: studentId,
            // Also store StudentID from behavior info
            behaviorInfoStudentID: behaviorStudentID,
            // Flag to indicate this came from student's own data
            fromStudentOwnData: shouldIncludeAll,
          });
         
        }
      });
    });
    
  
    
    // For students/patients: behaviorData already filtered, so return all behaviors
    if (isStudentOrPatient) {
      
      return allBehaviors;
    }
    
    // For teachers/doctors: filter by target ID (already filtered above, but double-check)
    const filtered = allBehaviors.filter((b) => {
      const matchesStudentId = b.behaviorStudentId === targetId;
      const matchesStudentID = b.behaviorInfoStudentID === targetId;
      return matchesStudentId || matchesStudentID;
    });
    
    
    return filtered;
  }, [behaviorData, selectedStudent, selectedPatient, typeOfUser, user?.uid]);

  const behaviorsOnly = useMemo(() => {

    
    if (!filteredBehaviors || filteredBehaviors.length === 0) {
      console.warn('🔍 behaviorsOnly: filteredBehaviors is empty!', {
        filteredBehaviors,
        behaviorDataLength: behaviorData?.length,
        studentBehaviorDataLength: studentBehaviorData?.length,
      });
      return [];
    }
    
    const filtered = filteredBehaviors.filter(
      (b) => {
        const type = b?.info?.BehaviorDetails?.Type?.Type;
        const hasId = !!(b.id || b.docId);
        const isBehavior = type === 'Behavior';
        
        if (!hasId) {
          console.warn('🔍 behaviorsOnly: Behavior missing id/docId', {
            behaviorKeys: Object.keys(b),
            hasId: !!b.id,
            hasDocId: !!b.docId,
            type,
          });
        }
        
    
        
        return isBehavior && hasId;
      }
    );
   
    
    return filtered;
  }, [filteredBehaviors, behaviorData, studentBehaviorData]);
  
  const skillsOnly = useMemo(() => {
    const filtered = filteredBehaviors.filter(
      (b) => {
        const type = b?.info?.BehaviorDetails?.Type?.Type;
        const hasId = !!(b.id || b.docId);
        if (!hasId) {
          console.warn('🔍 skillsOnly: Skill missing id/docId', {
            behaviorKeys: Object.keys(b),
            hasId: !!b.id,
            hasDocId: !!b.docId,
          });
        }
        return type === 'Skill Acquisition' && hasId;
      }
    );
   
    return filtered;
  }, [filteredBehaviors]);

  // Effects
  useEffect(() => {
    if (patients && patients.length > 0) {
      const dataArray = patients.map((each) => ({
        ...each.patientInfo,
        id: each.patientId,
      }));
      setPatientData(dataArray);
    }
  }, [patients]);

  useEffect(() => {
    // For students/patients, use their own UID
    // For teachers, use selectedStudent
    // For doctors, use selectedPatient
    const targetId = ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient
      ? user?.uid
      : ACM[typeOfUser]?.isTeacher 
      ? selectedStudent 
      : ACM[typeOfUser]?.isDoctor 
      ? selectedPatient 
      : null;
    // For students: collect ALL goals that belong to them (multiple items might match)
    // For teachers/doctors: find the specific student object
    let allMatchingGoals = [];
    
    if (ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) {
      
      // Collect all goals from filtered data that belong to this student
      (studentsGoalData || []).forEach((student, index) => {
        const idMatches = 
          student.id === targetId ||
          student.uid === targetId ||
          student.studentId === targetId;
        
        if (idMatches) {
          if (Array.isArray(student.dataCollection) && student.dataCollection.length > 0) {
            // Normalized structure with dataCollection
           
            allMatchingGoals.push(...student.dataCollection);
          } else if (student.docId && student.studentData && !Array.isArray(student.studentData)) {
            // Student structure that wasn't normalized yet
            const goalStudentId = student.studentData?.GoalDetails?.StudentID || student.studentData?.StudentID;
            if (goalStudentId === targetId) {

              allMatchingGoals.push({
                id: student.docId,
                info: student.studentData,
                goalData: student.studentData.goalData || null,
                activityLog: student.studentData.activityLog || [],
                isArchived: student.studentData.isArchived || false,
              });
            }
          }
        }
      });
      
      // Also check raw data for any goals we might have missed (in case normalization filtered them out)
      if (studentGoalData?.length > 0) {
      
        
        studentGoalData.forEach((item, index) => {
          if (item.docId && item.studentData && !Array.isArray(item.studentData)) {
            const goalStudentId = item.studentData?.GoalDetails?.StudentID || item.studentData?.StudentID;
            if (goalStudentId === targetId) {
              // Check if we already have this goal
              const alreadyAdded = allMatchingGoals.some(g => g.id === item.docId);
              if (!alreadyAdded) {
               
                allMatchingGoals.push({
                  id: item.docId,
                  info: item.studentData,
                  goalData: item.studentData.goalData || null,
                  activityLog: item.studentData.activityLog || [],
                  isArchived: item.studentData.isArchived || false,
                });
              }
            }
          }
        });
      }
      
    
    } else {
      // For teachers/doctors: find the specific student object
      let filteredStudent = (studentsGoalData || []).find((student, index) => {
        const idMatches = 
          student.id === targetId ||
          student.uid === targetId ||
          student.studentId === targetId ||
          student.docId === targetId;
        
        if (idMatches) {
          
          return true;
        }
        
        if (Array.isArray(student.dataCollection)) {
          const hasMatchingGoal = student.dataCollection.some(
            (goal) =>
              goal?.info?.GoalDetails?.StudentID === targetId ||
              goal?.info?.StudentID === targetId
          );
          if (hasMatchingGoal) {
            return true;
          }
        }
        
        if (student.studentData && !Array.isArray(student.studentData)) {
          const studentId = 
            student.studentData?.GoalDetails?.StudentID ||
            student.studentData?.StudentID;
          if (studentId === targetId) {
            
            return true;
          }
        }
        
        return false;
      });
      
      // If found, extract goals from dataCollection
      if (filteredStudent && Array.isArray(filteredStudent.dataCollection)) {
        allMatchingGoals = filteredStudent.dataCollection;
      }
    }

    // For teachers/doctors: if no goals found in filtered data, check raw data
    if (allMatchingGoals.length === 0 && !(ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) && studentGoalData?.length > 0) {
      
      const rawStudent = studentGoalData.find((student, index) => {
        const idMatches = 
          student.id === targetId ||
          student.uid === targetId ||
          student.studentId === targetId ||
          student.docId === targetId;
        
        if (idMatches) {
        
          return true;
        }
        
        if (Array.isArray(student.dataCollection)) {
          const hasMatch = student.dataCollection.some(
            (goal) =>
              goal?.info?.GoalDetails?.StudentID === targetId ||
              goal?.info?.StudentID === targetId
          );
          if (hasMatch) {
            return true;
          }
        }
        
        if (student.studentData && !Array.isArray(student.studentData)) {
          const studentId = 
            student.studentData?.GoalDetails?.StudentID ||
            student.studentData?.StudentID;
          if (studentId === targetId) {
            
            return true;
          }
        }
        
        return false;
      });
      
      if (rawStudent) {
        
        // Found in raw data - for video upload, show ALL goals for this student
        const newItem = { ...rawStudent };
        
        // Handle student structure (docId + studentData)
        if (rawStudent.docId && rawStudent.studentData && !Array.isArray(rawStudent.studentData)) {
          const goalStudentId = rawStudent.studentData?.GoalDetails?.StudentID || rawStudent.studentData?.StudentID;
          if (goalStudentId === targetId && !rawStudent.studentData.isArchived) {
            allMatchingGoals.push({
              id: rawStudent.docId,
              info: rawStudent.studentData,
              goalData: rawStudent.studentData.goalData || null,
              activityLog: rawStudent.studentData.activityLog || [],
              isArchived: false,
            });
          }
        } else if (Array.isArray(newItem.dataCollection)) {
          // For video upload: if goal belongs to selected student, show it
          // Only filter out archived goals
          const filtered = newItem.dataCollection.filter((collection) => {
            if (collection.isArchived === true) return false;
            
            // For teachers/doctors in video upload context:
            // If the goal's StudentID matches the selected student, show it
            const studentId = collection.info?.GoalDetails?.StudentID || collection.info?.StudentID;
            if (studentId === targetId) {
              return true; // Goal belongs to selected student - show it
            }
            
            // Also show if teacher has any form of access (owner, shared, pending)
            if (!collection.info || !collection.info.ShareWithTeam) {
              return collection.info?.GoalDetails?.GoalAuthor === user.uid;
            }
            const { EditAccess = [], ViewOnlyAccess = [] } = collection.info.ShareWithTeam;
            const isOwner = collection.info?.GoalDetails?.GoalAuthor === user.uid;
            const hasAccess = EditAccess.some(a => a.uid === user.uid) ||
                            ViewOnlyAccess.some(a => a.uid === user.uid);
            return isOwner || hasAccess;
          });
        
          
          allMatchingGoals.push(...filtered);
        }
      }
    }

    // Filter out archived goals
    const filteredGoals = allMatchingGoals.filter(
      (goal) => goal.isArchived !== true
    );


    setGoalsDropDown(filteredGoals || []);
  }, [selectedStudent, selectedPatient, studentsGoalData, studentGoalData, typeOfUser, user?.uid]);

  // Restore teacher fetching logic for peopleToShareWith
  useEffect(() => {
    const fetchTeachers = async () => {
      if (!selectedStudent) return;
      const result = await fetchingTeachersForStudents(selectedStudent);
      const filteredData = result?.filter((item) => item.uid !== user?.uid);
      setTeachers(filteredData);
    };
    if (ACM[typeOfUser]?.isTeacher) {
      fetchTeachers();
    }
  }, [selectedStudent, preRegisteredUsers, typeOfUser, user?.uid]);

  // Computed values
  const mergedData = ACM[typeOfUser]?.isTeacher
    ? [...students, ...preRegisteredUsers]
    : [...patientData, ...preRegisteredUsers];

  const isPreRegistered = (id) => {
    return preRegisteredUsers.some((student) => student.id === id);
  };

  const peopleToShareWith = [
    ...(teachers || []),
    !isPreRegistered(selectedStudent)
      ? {
          uid: 'Parent/Guardian',
          info: { fullname: 'Parent/Guardian' },
        }
      : null,
  ].filter(Boolean);

  return {
    // Data state
    patientData,
    setPatientData,
    teachers,
    setTeachers,
    goalsDropDown,
    setGoalsDropDown,
    
    // Fetched data
    students,
    preRegisteredUsers,
    studentsGoalData,
    behaviorData,
    filteredBehaviors,
    behaviorsOnly,
    skillsOnly,
    teacherData,
    
    // Computed values
    mergedData,
    peopleToShareWith,
  };
};

export default useUploadDataState;

